import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import type { AltchaChallenge, AltchaSolution } from '@shared/auth';
import { useEffect, useRef, useState, type JSX } from 'react';

interface AltchaWidgetProps {
  challenge: AltchaChallenge;
  onSolved: (solution: AltchaSolution) => void;
}

async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function AltchaWidget({ challenge, onSolved }: AltchaWidgetProps): JSX.Element {
  const [progress, setProgress] = useState(0);
  const [solved, setSolved] = useState(false);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    const start = performance.now();
    (async () => {
      // Iterate over candidate numbers, hashing sha256(salt + number) until it matches challenge.
      // Worker would be cleaner, but for MVP simplicity we yield to the event loop every N iters.
      const batchSize = 200;
      for (let n = 0; n <= challenge.maxnumber; n += batchSize) {
        if (cancelled.current) return;
        const end = Math.min(n + batchSize, challenge.maxnumber);
        for (let i = n; i <= end; i++) {
          const candidate = await sha256Hex(`${challenge.salt}${i}`);
          if (candidate === challenge.challenge) {
            setProgress(100);
            setSolved(true);
            onSolved({
              algorithm: 'SHA-256',
              challenge: challenge.challenge,
              number: i,
              salt: challenge.salt,
              signature: challenge.signature,
              took: performance.now() - start,
            });
            return;
          }
        }
        setProgress(Math.min(100, Math.floor((end / challenge.maxnumber) * 100)));
        await new Promise((r) => setTimeout(r, 0));
      }
    })();
    return () => {
      cancelled.current = true;
    };
  }, [challenge, onSolved]);

  return (
    <Box sx={{ mt: 1, mb: 1, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {solved ? 'Verification complete' : 'Verifying you are human…'}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ mt: 0.5 }}
        color={solved ? 'success' : 'primary'}
      />
    </Box>
  );
}
